/**
 * De brug van het voedingsblok naar de reactietegel.
 *
 * De knop "Zet in antwoord" schuift een uitkomst naar de tegel van
 * bouwer 2. Die tegel bestond nog niet toen dit gebouwd werd, dus de
 * koppeling loopt via deze kleine store in plaats van via een prop
 * door een boom die nog niet staat.
 *
 * Bewust geen react-afhankelijkheid en bewust geen `localStorage`: een
 * voorstel is iets van dít moment op dít scherm. Overleeft het een
 * herlaadbeurt, dan plakt het later in een gesprek waar het niet over
 * ging.
 *
 * Drie regels die het contract dragen:
 *
 * 1. Een voorstel hoort bij één klant. De tegel negeert een voorstel
 *    van een andere klant in plaats van de verkeerde tekst te plakken.
 * 2. `neemVoorstel` leegt de brug. Twee keer plakken kan niet, en dat
 *    is een eigenschap en geen toeval.
 * 3. Een nieuw voorstel overschrijft het vorige. Er staat er nooit
 *    meer dan één klaar, dus het scherm kan altijd tonen wát er
 *    klaarstaat.
 */

export interface AntwoordVoorstel {
  /** Vaste bron-sleutel; nu altijd het voedingsblok. */
  bron: "voedingsblok";
  /** De klant waar dit voorstel bij hoort. */
  klantId: string;
  /** Platte tekst, in het Nederlands en af. Nooit html. */
  tekst: string;
  /** Wanneer het klaargezet is (ISO-8601, UTC). */
  moment: string;
}

type Luisteraar = (voorstel: AntwoordVoorstel | null) => void;

let huidig: AntwoordVoorstel | null = null;
const luisteraars = new Set<Luisteraar>();

function meld(): void {
  for (const luisteraar of luisteraars) {
    // Eén luisteraar die klapt mag de rest niet meeslepen.
    try {
      luisteraar(huidig);
    } catch {
      // Bewust stil: dit is een melding, geen handeling.
    }
  }
}

/** Zet een voorstel klaar. Overschrijft wat er stond. */
export function zetInAntwoord(voorstel: AntwoordVoorstel): void {
  huidig = voorstel;
  meld();
}

/** Wat staat er nu klaar? `null` als er niets is. */
export function huidigVoorstel(): AntwoordVoorstel | null {
  return huidig;
}

/**
 * Haal het voorstel op en leeg de brug.
 *
 * Geeft `null` als er niets klaarstaat, of als wat er klaarstaat bij
 * een andere klant hoort. In dat laatste geval blijft het voorstel
 * staan: het is niet van jou, dus je gooit het ook niet weg.
 */
export function neemVoorstel(klantId: string): AntwoordVoorstel | null {
  if (huidig === null || huidig.klantId !== klantId) return null;
  const voorstel = huidig;
  huidig = null;
  meld();
  return voorstel;
}

/** Luister op wijzigingen. Geeft de opzegfunctie terug. */
export function abonneer(luisteraar: Luisteraar): () => void {
  luisteraars.add(luisteraar);
  return () => {
    luisteraars.delete(luisteraar);
  };
}

/** Alleen voor toetsen: begin met een schone brug. */
export function leegBrug(): void {
  huidig = null;
  meld();
}
